import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const blobCode = `// Binary Foundations: Blobs and Files
// Creating a virtual file (Blob) in memory
const data = JSON.stringify({ project: 'FileFusion', status: 'Active' });
const blob = new Blob([data], { type: 'application/json' });

// Converting Blob to a temporary URL for reference (e.g., download/preview)
const blobUrl = URL.createObjectURL(blob);
console.log("Internal Resource Link:", blobUrl);

// CRITICAL: Memory management. Revoke the URL when no longer needed.
// URL.revokeObjectURL(blobUrl);`;

const readerCode = `// Memory-Efficient Reading: FileReader
async function processLogs(file) {
    const reader = new FileReader();

    // Reading as text for logs/config
    reader.readAsText(file);

    reader.onload = (event) => {
        const content = event.target.result;
        console.log("First 100 characters:", content.substring(0, 100));
    };

    reader.onerror = () => {
        console.error("Transmission Error: File access blocked or corrupted.");
    };
}`;

const slicingCode = `// High-Performance I/O: File Slicing
function uploadInChunks(file) {
    const CHUNK_SIZE = 1024 * 1024; // 1MB Slices
    let offset = 0;

    while (offset < file.size) {
        // 'slice' does not copy data; it creates a pointer to a sub-range
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        sendToServer(chunk); // Process or upload specifically this byte-range
        offset += CHUNK_SIZE;
    }
}`;

const JSFileAPI = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Binary & Data</div>
                <h1>The File API: Engineering Binary I/O</h1>
                <p className="hero-lead">
                    Level up from strings to raw binary data. Master the File and Blob APIs to build
                    high-performance upload systems, memory-efficient preview engines, and
                    sophisticated client-side file processors.
                </p>
            </div>

            <div className="content-section">
                <h2>The Binary Foundation: Blobs and Files</h2>
                <p>
                    In the browser, files aren't just paths; they are **Blobs** (Binary Large Objects).
                    A `File` is simply a specialized Blob with metadata like a name and an
                    extension. Understanding how to wrap and unwrap these binary sequences is
                    essential for modern engineering tasks like image resizing, PDF generation,
                    or encrypted data transmission.
                </p>
                <CodeBlock code={blobCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Memory-Efficient Reading: FileReader</h2>
                <p>
                    To inspect the contents of a user-selected file, we use the `FileReader`. It provides
                    asynchronous methods to read data into different formats: `readAsText`,
                    `readAsDataURL` (base64 strings), and `readAsArrayBuffer` (raw memory).
                </p>
                <ProTip><p>
                        Never use `FileReader.readAsDataURL` for large images (e.g., {">"}2MB).
                        Base64 strings are 33% larger than the original binary and put heavy pressure
                        on the main thread. Instead, use `URL.createObjectURL(file)`, which handles the
                        binary reference directly through the browser's internal resource system.
                    </p></ProTip>
                <CodeBlock code={readerCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>High-Performance I/O: File Slicing</h2>
                <p>
                    When dealing with multi-gigabyte files (e.g., video uploads), loading the entire
                    file into memory will crash the browser tab. The `Blob.slice()` method allows
                    you to carve a file into smaller **chunks** without actually copying the physical
                    bits in memory. This is the foundation of "Resumable Uploads".
                </p>
                <CodeBlock code={slicingCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Security Protocols: File Sandboxing</h2>
                <p>
                    The browser's security model (the Sandbox) strictly prevents JavaScript from
                    reading files without explicit user action (like a "click" on an input or a
                    drag-and-drop event). You cannot "guess" a user's file path or read from their
                    hard drive programmatically. This integrity is what allows users to safely
                    interact with potentially malicious scripts on the web.
                </p>
            </div>

            <KeyFacts><ul>
                    <li>**Efficiency:** Use `URL.createObjectURL` for fast previews without Base64 overhead.</li>
                    <li>**Memory:** Revoke Object URLs using `URL.revokeObjectURL` once the image is loaded.</li>
                    <li>**Scale:** Slice large files into 1MB-5MB chunks for resumable network uploads.</li>
                    <li>**Safety:** Always validate the `file.type` (MIME) before processing to prevent XSS.</li>
                    <li>**Feedback:** Use `FileReader.onprogress` to drive technical progress bars.</li>
                    <li>**Architecture:** Prefer `ArrayBuffer` when performing direct binary manipulation (WebAssembly).</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSFileAPI;


